import { useEffect, useState, type FormEvent } from 'react';
import {
  useCreateTaxOption,
  useDeleteTaxOption,
  useUpdateTaxOption,
} from '@repo/queries';
import type { TaxOption, TaxOptionKind } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';

interface TaxOptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxOption?: TaxOption | null;
}

export function TaxOptionDialog({ open, onOpenChange, taxOption }: TaxOptionDialogProps) {
  const create = useCreateTaxOption();
  const update = useUpdateTaxOption();
  const remove = useDeleteTaxOption();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [kind, setKind] = useState<TaxOptionKind>('add');
  const [region, setRegion] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (taxOption) {
      setName(taxOption.name);
      setRate(String(taxOption.rate));
      setKind(taxOption.kind);
      setRegion(taxOption.region ?? '');
      setDescription(taxOption.description ?? '');
    } else {
      setName('');
      setRate('');
      setKind('add');
      setRegion('');
      setDescription('');
    }
    setError(null);
  }, [taxOption, open]);

  const editing = !!taxOption;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const rateNum = Number(rate);
    if (!Number.isFinite(rateNum) || rateNum < 0 || rateNum > 100) {
      setError('Rate must be between 0 and 100.');
      return;
    }
    try {
      if (editing && taxOption) {
        await update.updateTaxOption({
          taxOptionId: taxOption.id,
          updates: {
            name,
            rate: rateNum,
            kind,
            ...(region ? { region } : { region: undefined }),
            ...(description ? { description } : { description: undefined }),
          },
        });
        toast({ title: 'Tax option saved', variant: 'success' });
      } else {
        await create.createTaxOption({
          name,
          rate: rateNum,
          kind,
          ...(region ? { region } : {}),
          ...(description ? { description } : {}),
        });
        toast({ title: 'Tax option created', variant: 'success' });
      }
      onOpenChange(false);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  async function onDelete() {
    if (!taxOption) return;
    if (typeof window !== 'undefined' && !window.confirm(`Delete ${taxOption.name}?`)) return;
    try {
      await remove.deleteTaxOption(taxOption.id);
      toast({ title: 'Tax option deleted', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit tax option' : 'New tax option'}</DialogTitle>
          <DialogDescription>
            Reusable rate presets you can apply on the invoice composer.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field label="Name" htmlFor="name" required>
            <Input
              id="name"
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Rate (%)" htmlFor="rate" required error={error ?? undefined}>
              <Input
                id="rate"
                type="number"
                min={0}
                max={100}
                step="any"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                required
              />
            </Field>
            <Field label="Kind" htmlFor="kind">
              <Select value={kind} onValueChange={(v) => setKind(v as TaxOptionKind)}>
                <SelectTrigger id="kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add to total (e.g. VAT)</SelectItem>
                  <SelectItem value="subtract">Subtract from total (withholding)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Region" htmlFor="region" hint="Country or sub-region this applies to.">
            <Input id="region" value={region} onChange={(e) => setRegion(e.target.value)} />
          </Field>
          <Field label="Description" htmlFor="description">
            <Textarea
              id="description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <DialogFooter className="justify-between">
            {editing ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onDelete}
                disabled={remove.loading}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.loading || update.loading}>
                {editing ? 'Save' : 'Create'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
